import { FrameBuffer, TextureUtils } from "webgl-framework";
import { mat4 } from "gl-matrix";
import { BlurSize } from "./GaussianBlurRenderPass";
import { SsaoShader } from "./shaders/SsaoShader";
import { SsaoFastShader } from "./shaders/SsaoFastShader";
import { SsaoBlurRenderPass } from "./SsaoBlurRenderPass";

/**
 * Config for the SSAO pass.
 */
export interface SsaoRenderConfig {
    /** Sampling radius, in full-resolution canvas pixels (independent of the AO buffer resolution). */
    radius: number;
    /** View-space distance at which occlusion contribution fades to zero. */
    depthRange: number;
    /** Minimal horizon cosine to count as occlusion. */
    bias: number;
    /** Occlusion strength multiplier. */
    intensity: number;
    /** Blur kernel size; undefined uses the AO texture as it is (unfiltered). */
    blurSize: BlurSize | undefined;
}

/**
 * Helper class to render scene depth and screen-space ambient occlusion from it.
 * Produces a ready-to-use AO texture: render depth into the depth FBO, then render SSAO
 * and optionally blur it via [renderSsao].
 */
export class SsaoRenderPass {
    private textureAoColor?: WebGLTexture;
    private textureAoDepth?: WebGLTexture;
    private fboAoDepth?: FrameBuffer;
    private blurPass?: SsaoBlurRenderPass;

    private quadTriangles: Float32Array;
    private vertexDataBuffer?: WebGLBuffer | null;
    private identity = mat4.create();

    public width = 0;
    public height = 0;
    public resolutionScale: number;

    /**
     * Draws the AO with the cheap [SsaoFastShader] instead of the regular [SsaoShader]. Read on
     * every draw, so it can follow a setting of the scene.
     */
    public lowQuality = false;

    /** Both shaders are compiled the first time one of them is asked for. */
    private shaderRegular?: SsaoShader;
    private shaderFast?: SsaoFastShader;

    // Full-resolution canvas dimensions; the sampling radius is expressed in these pixels, so the
    // AO look does not depend on the AO buffer resolution.
    private canvasWidth = 0;
    private canvasHeight = 0;

    constructor(private gl: WebGL2RenderingContext, canvasWidth: number, canvasHeight: number, scale: number) {
        this.resolutionScale = scale;
        this.quadTriangles = new Float32Array([
            // X, Y, Z, U, V
            -1.0, -1.0, 0.0, 0.0, 0.0, // 0. left-bottom
            1.0, -1.0, 0.0, 1.0, 0.0, // 1. right-bottom
            -1.0, 1.0, 0.0, 0.0, 1.0, // 2. left-top
            1.0, 1.0, 0.0, 1.0, 1.0, // 3. right-top
        ]);
        this.resize(canvasWidth, canvasHeight);
    }

    /**
     * Recomputes AO size from the canvas dimensions and recreates all GL resources.
     */
    public resize(canvasWidth: number, canvasHeight: number): void {
        this.canvasWidth = canvasWidth;
        this.canvasHeight = canvasHeight;
        this.width = Math.max(1, Math.round(canvasWidth * this.resolutionScale));
        this.height = Math.max(1, Math.round(canvasHeight * this.resolutionScale));

        this.free();

        this.textureAoColor = TextureUtils.createNpotTexture(this.gl, this.width, this.height, false)!;
        this.textureAoDepth = this.createDepthTexture(this.width, this.height);

        this.fboAoDepth = new FrameBuffer(this.gl);
        this.fboAoDepth.textureHandle = this.textureAoColor;
        this.fboAoDepth.depthTextureHandle = this.textureAoDepth;
        this.fboAoDepth.createGLData(this.width, this.height);

        this.blurPass = new SsaoBlurRenderPass(this.gl, Math.min(this.width, this.height), this.width / this.height);

        this.vertexDataBuffer = this.gl.createBuffer();
        this.gl.bindBuffer(this.gl.ARRAY_BUFFER, this.vertexDataBuffer!);
        this.gl.bufferData(this.gl.ARRAY_BUFFER, this.quadTriangles, this.gl.STATIC_DRAW);
    }

    /**
     * Deletes owned GL resources.
     */
    public free(): void {
        if (this.textureAoColor !== undefined) {
            this.gl.deleteTexture(this.textureAoColor);
        }
        if (this.textureAoDepth !== undefined) {
            this.gl.deleteTexture(this.textureAoDepth);
        }
        if (this.fboAoDepth?.framebufferHandle) {
            this.gl.deleteFramebuffer(this.fboAoDepth.framebufferHandle);
        }
        if (this.vertexDataBuffer !== undefined) {
            this.gl.deleteBuffer(this.vertexDataBuffer);
        }
    }

    /**
     * Binds the AO depth FBO. Caller sets color/depth state and clears depth.
     */
    public switchToAoDepthFBO(): void {
        this.gl.bindFramebuffer(this.gl.FRAMEBUFFER, this.fboAoDepth!.framebufferHandle);
        this.gl.viewport(0, 0, this.width, this.height);
    }

    /**
     * Renders AO from the depth texture into the blur pass's offscreen FBO (never into the depth
     * FBO, so sampling the depth texture cannot form a feedback loop), then blurs it in place.
     */
    public renderSsao(invProjMatrix: mat4, config: SsaoRenderConfig): void {
        const gl = this.gl;

        const ssaoShader = this.selectShader();

        this.blurPass!.switchToOffscreenFBO();
        gl.disable(gl.CULL_FACE);
        gl.disable(gl.BLEND);
        gl.disable(gl.DEPTH_TEST);
        gl.depthMask(false);
        gl.colorMask(true, true, true, true);

        ssaoShader.use();

        gl.activeTexture(gl.TEXTURE0);
        gl.bindTexture(gl.TEXTURE_2D, this.textureAoDepth!);
        gl.uniform1i(ssaoShader.sDepth!, 0);

        gl.uniformMatrix4fv(ssaoShader.invProjMatrix!, false, invProjMatrix);
        const texelX = 1 / this.width;
        const texelY = 1 / this.height;
        gl.uniform2f(ssaoShader.texelSize!, texelX, texelY);
        // Radius is in full-resolution canvas pixels; convert to UV units so the sampling radius
        // stays constant regardless of the AO buffer resolution.
        gl.uniform2f(ssaoShader.radiusUvSize!, config.radius / this.canvasWidth, config.radius / this.canvasHeight);
        // gl.uniform2f(shader.radiusUvSize!, config.radius * texelX, config.radius * texelY);
        gl.uniform1f(ssaoShader.depthRange!, config.depthRange);
        gl.uniform1f(ssaoShader.bias!, config.bias);
        gl.uniform1f(ssaoShader.intensity!, config.intensity);
        // The offsets go with the shader which is drawn with: its sample count sizes them.
        gl.uniform2fv(ssaoShader.sampleOffsets!, ssaoShader.precomputedSampleOffsets);

        // fullscreen quad
        gl.bindBuffer(gl.ARRAY_BUFFER, this.vertexDataBuffer!);
        gl.enableVertexAttribArray(ssaoShader.rm_Vertex!);
        gl.vertexAttribPointer(ssaoShader.rm_Vertex!, 3, gl.FLOAT, false, 5 * 4, 0);
        gl.enableVertexAttribArray(ssaoShader.rm_TexCoord0!);
        gl.vertexAttribPointer(ssaoShader.rm_TexCoord0!, 2, gl.FLOAT, false, 5 * 4, 4 * 3);
        gl.uniformMatrix4fv(ssaoShader.view_proj_matrix!, false, this.identity);
        gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);

        gl.depthMask(true);

        // Without a kernel size the AO written above is used as it is, no blur pass runs.
        if (config.blurSize !== undefined) {
            this.blurPass!.blur(1.0, config.blurSize);
        }
    }

    /**
     * The shader the AO is drawn with: the regular one, or the cheap one while [lowQuality] is
     * set. Both are compiled once, on first use.
     */
    private selectShader(): SsaoShader {
        if (this.lowQuality) {
            this.shaderFast ??= new SsaoFastShader(this.gl);
            return this.shaderFast;
        }

        this.shaderRegular ??= new SsaoShader(this.gl);
        return this.shaderRegular;
    }

    /**
     * Ready-to-use (blurred) SSAO texture.
     */
    public get texture(): WebGLTexture {
        return this.blurPass!.texture!;
    }

    public get depthTexture(): WebGLTexture {
        return this.textureAoDepth!;
    }

    private createDepthTexture(texWidth: number, texHeight: number): WebGLTexture {
        const gl = this.gl;
        const textureID = gl.createTexture()!;
        gl.bindTexture(gl.TEXTURE_2D, textureID);

        gl.texParameterf(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
        gl.texParameterf(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);

        // 24-bit depth is good enough, no need for 32-bit.
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.DEPTH_COMPONENT24, texWidth, texHeight, 0, gl.DEPTH_COMPONENT, gl.UNSIGNED_INT, null);

        return textureID;
    }
}
